import React, { useRef } from 'react';
import uuid from 'react-uuid';
import { CancelTokenSource } from 'axios';
import { HIDE_INPUT_FORM_DISCLAIMER_COUNT_THRESHOLD } from 'common/components/CopyMagic/constants';
import { UiResourceContextInterface } from './UiResourceContext.types';
import { setDeepState } from './helpers/setDeepState';

/*
UiResourceContext
  Stores UI state, the UiResourceContext is agnostic to which generator is active
*/

const noop = () => {};
const DEFAULTS = {
  open: false,
  sessionId: null,
  setOpen: noop,
  loading: false,
  setLoading: noop,
  showDiscardModal: false,
  setShowDiscardModal: noop,
  feedbackSentiment: { positive: null },
  setFeedbackSentiment: noop,
  error: null,
  setError: noop,
  resetError: noop,
  promptValues: { topic: '' },
  setPromptValues: noop,
  resetPromptValues: noop,
  cancelPendingRequest: noop,
  setRequestSource: noop,
  setGenerateAgain: noop,
  generateAgain: false,
  conversationAssistantOpenedCount: HIDE_INPUT_FORM_DISCLAIMER_COUNT_THRESHOLD,
  setConversationAssistantOpenedCount: noop,
} as const;

export const UiResourceContext = React.createContext<UiResourceContextInterface>(DEFAULTS);

export const useUiResourceContext = () => {
  const context = React.useContext(UiResourceContext);
  if (!context) throw new Error(`outside of provider`);
  return context;
};

export const UiResourceProvider = ({ children }: { children: React.ReactElement }) => {
  const providerStateRef = useRef<UiResourceContextInterface>(DEFAULTS);
  let setProviderStateFn: React.Dispatch<React.SetStateAction<UiResourceContextInterface>>;
  const requestSourceRef = useRef<CancelTokenSource | null>();

  const providerActions: Partial<UiResourceContextInterface> = {
    setOpen: (active) => {
      setDeepState(setProviderStateFn, ['open'], active);
      const newSessionId = active ? uuid() : null;
      setDeepState(setProviderStateFn, ['sessionId'], newSessionId);
    },
    setLoading: (active) => setDeepState(setProviderStateFn, ['loading'], active),
    setShowDiscardModal: (active) => setDeepState(setProviderStateFn, ['showDiscardModal'], active),
    setFeedbackSentiment: (value) => setDeepState(setProviderStateFn, ['feedbackSentiment'], value),
    setError: (message) => setDeepState(setProviderStateFn, ['error'], message),
    resetError: () => setDeepState(setProviderStateFn, ['error'], DEFAULTS.error),
    setPromptValues: (values) => setDeepState(setProviderStateFn, ['promptValues'], values),
    resetPromptValues: () =>
      setDeepState(setProviderStateFn, ['promptValues'], DEFAULTS.promptValues),
    cancelPendingRequest: () => {
      if (requestSourceRef.current) {
        requestSourceRef.current.cancel();
        requestSourceRef.current = null;
      }
    },
    setRequestSource: (source) => {
      requestSourceRef.current = source;
    },
    setGenerateAgain: (active) => setDeepState(setProviderStateFn, ['generateAgain'], active),
    setConversationAssistantOpenedCount: (count) =>
      setDeepState(setProviderStateFn, ['conversationAssistantOpenedCount'], count),
  };

  const initialState = Object.assign({}, DEFAULTS, providerActions);
  const [providerState, setProviderState] =
    React.useState<UiResourceContextInterface>(initialState);
  providerStateRef.current = providerState;
  setProviderStateFn = setProviderState;

  return <UiResourceContext.Provider value={providerState}>{children}</UiResourceContext.Provider>;
};
